import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { LoadingState } from '@orbind/ui'
import type { CanaisDeAcesso } from '@orbind/core'
import { api, apiOrigin, webTokenStorage } from './api.ts'
import { canalAtual } from './canal.ts'
import { bloqueioDoCanal } from '../features/app/canal-bloqueado.ts'
import { CanalBloqueado } from '../features/app/CanalBloqueado.tsx'

export type SessionUser = {
  id: string
  name: string
  email: string
  avatarUrl: string | null
  /** Foto enviada no perfil (blob no servidor) — quando existe, vence a avatarUrl. */
  hasAvatar?: boolean
  /** Muda a cada alteracao do cadastro: cache-buster da url da foto. */
  avatarVersion?: string
  companyAreaId?: string | null
  localeId?: string | null
  /**
   * Layout DA PESSOA (largura de paineis, o que fica aberto). Vem da conta e
   * nao do navegador: vale no desktop e na web, e nao vaza entre colegas que
   * dividem o mesmo computador. Gravado por `usePreferenciaDeLayout`.
   */
  layoutPrefs?: unknown
  /**
   * Ja entrou alguma vez pelo app INSTALADO (SPEC-20260925-1628). Falso e o
   * unico sinal confiavel de que a pessoa nao tem o desktop: o navegador nao
   * enxerga a maquina, mas o shell instalado se identifica ao conectar.
   */
  usouDesktop?: boolean
  /** Super admin da instalacao: libera o Console no menu do usuario. */
  isSuperAdmin?: boolean
  /**
   * Por onde pode usar o Orbind neste workspace (SPEC-20261008-1104-canais-de-acesso):
   * a regra do colaborador, ou o padrão do workspace.
   */
  canais?: { permitidos: CanaisDeAcesso; regraPropria: boolean }
}

/** Imagem a exibir para o usuario: foto enviada > url externa > null (iniciais). */
export function sessionAvatarSrc(user: Pick<SessionUser, 'id' | 'avatarUrl' | 'hasAvatar' | 'avatarVersion'>) {
  if (user.hasAvatar) {
    return `${apiOrigin()}/public/member-avatar/${user.id}?v=${encodeURIComponent(user.avatarVersion ?? '')}`
  }
  return user.avatarUrl
}

const SessionContext = createContext<SessionUser | null>(null)

/**
 * Guarda de rota autenticada.
 *
 * Valida a sessao contra `/auth/me` em vez de confiar no token guardado: um
 * token expirado ou de um usuario desativado ainda esta no localStorage, e so o
 * servidor sabe disso.
 */
/**
 * Super admin que escolheu "Entrar mesmo assim" na tela de canal fechado:
 * vale para ESTA aba (sessionStorage), e por pessoa — fechar a aba ou entrar
 * de novo mostra a tela outra vez.
 */
const CHAVE_PASSE_DO_CANAL = 'orbind.canal-liberado'
function lerPasseDoCanal(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_PASSE_DO_CANAL)
  } catch {
    return null
  }
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [passeDoCanal, setPasseDoCanal] = useState(lerPasseDoCanal)
  const { data, isPending, isError } = useQuery({
    // o TOKEN entra na chave: sair e entrar com outra pessoa na mesma aba
    // reaproveitava por 5 min o /me da anterior — nome, super admin e os
    // canais de acesso dela (o "só desktop" não barrava, 2026-10-08)
    queryKey: ['session', webTokenStorage.get()],
    queryFn: async () => (await api.auth.me.$get()).json(),
    enabled: api.isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  if (!api.isAuthenticated || isError) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  if (isPending) return <LoadingState variant="detail" label="Verificando sessão" />

  // canal não liberado neste workspace: a tela de bloqueio toma o lugar do
  // app — no caso do desktop, com o download (SPEC-20261008-1104-canais-de-acesso)
  const atual = canalAtual()
  const bloqueio = bloqueioDoCanal(data.user.canais?.permitidos, atual)
  const superAdmin = data.user.isSuperAdmin === true
  if (bloqueio && !(superAdmin && passeDoCanal === data.user.id)) {
    return (
      <CanalBloqueado
        bloqueio={bloqueio}
        atual={atual}
        // o super admin vê a regra valendo, mas não fica trancado fora do
        // Console: pode entrar mesmo assim, só nesta aba
        onEntrarMesmoAssim={
          superAdmin
            ? () => {
                try {
                  sessionStorage.setItem(CHAVE_PASSE_DO_CANAL, data.user.id)
                } catch {
                  // sem storage o passe vale só até recarregar
                }
                setPasseDoCanal(data.user.id)
              }
            : undefined
        }
      />
    )
  }

  return <SessionContext.Provider value={data.user}>{children}</SessionContext.Provider>
}

export function useSession(): SessionUser {
  const user = useContext(SessionContext)
  if (!user) throw new Error('useSession precisa estar dentro de <RequireAuth>')
  return user
}
